import { IExternalStorageProvider } from "../../adapters/driven/providers/externalStorageProvider";
import { IAlertRepository } from "../../adapters/driven/repositories/alertRepository";

export const AlertServiceImplFactory = ({
  alertRepository,
  externalStorageProvider,
}: {
  alertRepository: IAlertRepository;
  externalStorageProvider: IExternalStorageProvider;
}) => {
  const getAlertDetails: IGetAlertDetail = async ({
    clinicId,
    residentId,
    alertId,
  }) => {
    try {
      const response = await alertRepository.getAlertDetails({
        clinicId,
        residentId,
        alertId,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const getAlertFile: IGetAlertFile = async ({ fileName }) => {
    try {
      const parseFilePathName = `public/clinicPatient/${fileName}`;
      const response = await externalStorageProvider.getFile({
        bucket: "resident-face-sheet",
        fileName: parseFilePathName,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const getAlertList: IGetAlertList = async ({
    clinicId,
    floorIds,
    consent,
    levels,
    read,
    unread,
    end,
    start,
    search,
    page,
    page_size,
    pagination_flag,
  }) => {
    try {
      const payload: IGetAlertListAPIPayload = {
        clinic_id: clinicId,
        floor_ids: floorIds,
        read_alert: read ? 1 : 0,
        unread_alert: unread ? 1 : 0,
        level: levels,
        page,
        page_size,
        pagination_flag,
        consent_statuses: consent || ["1", "2"],
      };

      if (start) payload.start_date = start;
      if (end) payload.end_date = end;
      if (search) payload.search_txt = search;

      const response = await alertRepository.getAlertListAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const addAlertComment: IAddAlertComment = async ({
    alertId,
    clinicId,
    notes,
  }) => {
    try {
      const response = await alertRepository.addAlertComment({
        alert_id: alertId.toString(),
        clinic_id: clinicId.toString(),
        notes,
        acknowledgement: true,
        app_type: "cip",
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getCombinedAlertService: IGetCombinedAlertListService = async (
    payload
  ) => {
    try {
      const response = await alertRepository.getCombinedAlertAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientAlertService: IGetPatientAlertService = async (payload) => {
    try {
      const response = await alertRepository.getPatientAlertAPI(payload);
      if (response.data.length > 0) {
        return {
          success: true,
          data: { data: response.data[0] },
        };
      }
      return {
        success: false,
        error: { message: "Not Found" },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const addAlertAckService: IAddAlertAckService = async (payload) => {
    try {
      const response = await alertRepository.addAlertAck(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getFlagListService: IGetFlagListService = async (payload) => {
    try {
      const response = await alertRepository.getFlagListAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getInboxFlagsByReviewStatusService: IGetInboxFlagsByReviewStatusService =
    async (payload) => {
      try {
        const response = await alertRepository.getInboxFlagsByReviewStatusAPI(
          payload
        );
        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getPatientFlagsService: IGetPatientFlagsService = async (payload) => {
    try {
      const response = await alertRepository.getPatientFlagsAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getFlagAlertClinicalService: IGetFlagAlertClinicalService = async (
    payload
  ) => {
    try {
      const response = await alertRepository.getFlagAlertClinicalAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const hideFlagService: IHideFlagService = async (payload) => {
    try {
      const response = await alertRepository.hideFlag(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getAlertDetails,
    getAlertFile,
    getAlertList,
    addAlertComment,
    getCombinedAlertService,
    addAlertAckService,
    getPatientAlertService,
    getFlagListService,
    getFlagAlertClinicalService,
    hideFlagService,
    getInboxFlagsByReviewStatusService,
    getPatientFlagsService,
  };
};
